import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/* ------------------------------------------------------------------ */
/*  Discriminated amount validator.                                    */
/*                                                                     */
/*  Replaces the per-form `Validators.pattern(/^\d+(\.\d{1,N})?$/)` +  */
/*  `Validators.min(...)` combo that collapsed every input mistake     */
/*  ("abc", "11.234222222", "0", "-5") into a single generic error.    */
/*                                                                     */
/*  Returns at most one of these mutually-exclusive error keys so the  */
/*  template can branch on which input rule the user broke:            */
/*    amountFormat   — not a valid decimal number                      */
/*    amountDecimals — too many digits after the decimal point         */
/*    amountNonZero  — non-positive when nonZero is required           */
/*    amountMin      — below the configured minimum                    */
/*    amountMax      — above the configured maximum                    */
/*                                                                     */
/*  min/max are CANONICAL DECIMAL STRINGS — never Numbers. Storing as  */
/*  Number leaks JS scientific-notation into the template: Number-based*/
/*  `0.00000001` renders as `1e-8`, surfacing "Minimum allowed amount  */
/*  is 1e-8" to end users. Strings round-trip safely.                  */
/* ------------------------------------------------------------------ */

export interface AmountValidatorOptions {
  /** Maximum digits allowed after the decimal point. */
  readonly maxDecimals: number;
  /**
   * Optional minimum as a canonical decimal string (e.g. '0.01',
   * '0.00000001'). Smaller values yield `amountMin`.
   */
  readonly min?: string;
  /**
   * Optional maximum as a canonical decimal string (e.g. '10000000').
   * Larger values yield `amountMax`.
   */
  readonly max?: string;
  /** When true, zero and negative values yield `amountNonZero`. */
  readonly nonZero?: boolean;
}

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

export function amountValidator(opts: AmountValidatorOptions): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }

    const str = typeof raw === 'number' ? String(raw) : String(raw).trim();
    if (!DECIMAL_RE.test(str)) {
      return { amountFormat: true };
    }

    const dot = str.indexOf('.');
    if (dot !== -1) {
      const decimals = str.length - dot - 1;
      if (decimals > opts.maxDecimals) {
        return { amountDecimals: { max: opts.maxDecimals, actual: decimals } };
      }
    }

    const value = Number(str);
    if (!Number.isFinite(value)) {
      return { amountFormat: true };
    }

    if (opts.nonZero && value <= 0) {
      return { amountNonZero: true };
    }

    if (opts.min !== undefined && value < Number(opts.min)) {
      return { amountMin: { min: opts.min, actual: str } };
    }

    if (opts.max !== undefined && value > Number(opts.max)) {
      return { amountMax: { max: opts.max, actual: str } };
    }

    return null;
  };
}
